import {
  Autocomplete,
  type AutocompleteProps,
  type AutocompleteRenderGetTagProps,
  Chip,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";

import { Option } from "api/common/types/common.types";
import { useFieldContext } from "components/Forms/AppForm";

import ErrorMessage from "../../helpers/ErrorMessage";

import { useNormalizedOptions } from "./UseNormalizedOptions";

type SelectFieldProps = Omit<
  AutocompleteProps<Option, true, true, false>,
  "renderInput"
> & {
  options: Option[] | string[];
  label: string;
  required?: boolean;
  disableTags?: boolean;
  getChipLabel?: (_option: Option) => string;
};

const MultiAutocompleteSelect = ({
  options,
  label,
  required = false,
  disableTags = false,
  getChipLabel = (opt) => opt.label,
  ...props
}: SelectFieldProps) => {
  const field = useFieldContext<Option[]>();

  const normalizedOptions = useNormalizedOptions(options);

  const renderTagOptions = {
    renderTags: disableTags
      ? (value: Option[]) => <ListOnlyRenderTag value={value} />
      : (value: Option[], getTagProps: AutocompleteRenderGetTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              label={getChipLabel(option)}
              key={option.value}
              size="small"
            />
          )),
  };

  return (
    <FormControl fullWidth size="small">
      <Autocomplete
        disablePortal
        size="small"
        options={normalizedOptions}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={field.state.meta.errors.length > 0}
            required={required}
          />
        )}
        value={field.state.value || []}
        onChange={(_e, value) => value && field.handleChange(value)}
        disableListWrap
        selectOnFocus
        clearOnBlur
        isOptionEqualToValue={(option, value) => option.value === value.value}
        {...renderTagOptions}
        handleHomeEndKeys
        slotProps={{
          popper: {
            sx: {
              boxBlur: "ghostwhite 0px -1px 8px 4px",
              ".MuiAutocomplete-option[aria-selected='true']": {
                backgroundColor: "#ebebeb",
              },
            },
          },
        }}
        {...props}
        multiple={true}
      />
      <ErrorMessage />
    </FormControl>
  );
};
export default MultiAutocompleteSelect;

interface DefaultRenderTagProps {
  value: Option[];
}

const ListOnlyRenderTag = ({ value }: DefaultRenderTagProps) => {
  return (
    <Typography sx={{ marginLeft: 1 }}>
      {value.map((v) => v.label)?.join(", ")}
    </Typography>
  );
};
